/// src/components/atomic-crm/root/CRM.tsx
import { useState, useEffect } from "react"; // ← Add this import at the top
import {
  CanAccess,
  CustomRoutes,
  localStorageStore,
  Resource,
  type AuthProvider,
  type DataProvider,
} from "ra-core";
import { Navigate, Route } from "react-router";
import { AdminContext, AdminUI } from "@/components/ds/admin/admin";
import { ForgotPasswordPage } from "@/components/supabase/forgot-password-page";
import { SetPasswordPage } from "@/components/supabase/set-password-page";
import { ChangePasswordPage } from "@/components/supabase/change-password-page";
import { OtpLoginPage } from "@/components/supabase/otp-login-page";

import companies from "../companies";
import contacts from "../contacts";
import Dashboard from "../dashboard/Dashboard";
//import DashboardV2 from "../dashboard/DashboardV2"; // ← Import

import deals from "../deals";
import invoices from "../invoices";
import { Layout } from "../layout/Layout";
import { SignupPage } from "../login/SignupPage";
import {
  authProvider as defaultAuthProvider,
  dataProvider as defaultDataProvider,
} from "../providers/supabase";
import sales from "../sales";
import { DatabasePage } from "../settings/DatabasePage";
import { SettingsPage } from "../settings/SettingsPage";
import { IntegrationsPage } from "../integrations/IntegrationsPage";
import type { ConfigurationContextValue } from "./ConfigurationContext";
import { ConfigurationProvider } from "./ConfigurationContext";

import { DataManagementPage } from "../settings/DataManagementPage";
import { OnboardingWizard } from "../onboarding/OnboardingWizard.tsx";

import {
  defaultCompanySectors,
  defaultContactGender,
  defaultDarkModeLogo,
  defaultDealCategories,
  defaultDealPipelineStatuses,
  defaultDealStages,
  defaultLightModeLogo,
  defaultNoteStatuses,
  defaultTaskPriorities,
  defaultTaskStatuses,
  defaultTaskTypes,
  defaultTitle,
} from "./defaultConfiguration";
import { i18nProvider } from "./i18nProvider";
import { StartPage } from "../login/StartPage.tsx";
import { DatabaseHealthCheck } from "./DatabaseHealthCheck";
// import { SDKProvider } from "./SDKProvider";
import { AIPage } from "../ai/AIPage";
import { Sparkles } from "lucide-react";
import { AISettingsProvider } from "./AISettingsProvider";

import activities from "../activities";

import { SetupWorkspace } from "../login/SetupWorkspace";
import { useNavigate } from "react-router-dom";

function PasswordResetHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("mode") === "set-password") {
      // Clear the query string from the URL so it's clean
      window.history.replaceState({}, document.title, "/");

      // Give Supabase 500ms to process the #access_token in the URL
      setTimeout(() => {
        navigate("/set-password", { replace: true });
      }, 500);
    }
  }, [navigate]);

  return null;
}

export type CRMProps = {
  dataProvider?: DataProvider;
  authProvider?: AuthProvider;
  disableTelemetry?: boolean;
} & Partial<ConfigurationContextValue>;

/**
 * CRM Component
 *
 * This component sets up and renders the main CRM application using `ra-core`. It provides
 * default configurations and themes but allows for customization through props. The component
 * wraps the application with a `ConfigurationProvider` to provide configuration values via context.
 *
 * @param {Array<ContactGender>} contactGender - The gender options for contacts used in the application.
 * @param {string[]} companySectors - The list of company sectors used in the application.
 * @param {RaThemeOptions} darkTheme - The theme to use when the application is in dark mode.
 * @param {string[]} dealCategories - The categories of deals used in the application.
 * @param {string[]} dealPipelineStatuses - The statuses of deals in the pipeline used in the application.
 * @param {DealStage[]} dealStages - The stages of deals used in the application.
 * @param {RaThemeOptions} lightTheme - The theme to use when the application is in light mode.
 * @param {string} logo - The logo used in the CRM application.
 * @param {NoteStatus[]} noteStatuses - The statuses of notes used in the application.
 * @param {string[]} taskTypes - The types of tasks used in the application.
 * @param {Object[]} taskPriorities - The priorities of tasks used in the application.
 * @param {Object[]} taskStatuses - The statuses of tasks used in the application.
 * @param {string} title - The title of the CRM application.
 *
 * @returns {JSX.Element} The rendered CRM application.
 *
 * @example
 * // Basic usage of the CRM component
 * import { CRM } from '@/components/atomic-crm/dashboard/CRM';
 *
 * const App = () => (
 *     <CRM
 *         logo="/path/to/logo.png"
 *         title="My Custom CRM"
 *         lightTheme={{
 *             ...defaultTheme,
 *             palette: {
 *                 primary: { main: '#0000ff' },
 *             },
 *         }}
 *     />
 * );
 *
 * export default App;
 */
export const CRM = ({
  contactGender = defaultContactGender,
  companySectors = defaultCompanySectors,
  companyLifecycleStages,
  companyTypes,
  companyQualificationStatuses,
  companyRevenueRanges,
  dealCategories = defaultDealCategories,
  dealPipelineStatuses = defaultDealPipelineStatuses,
  dealStages = defaultDealStages,
  darkModeLogo = defaultDarkModeLogo,
  lightModeLogo = defaultLightModeLogo,
  noteStatuses = defaultNoteStatuses,
  taskTypes = defaultTaskTypes,
  taskPriorities = defaultTaskPriorities,
  taskStatuses = defaultTaskStatuses,
  title = defaultTitle,
  externalHeartbeatStatuses,
  internalHeartbeatStatuses,
  dataProvider = defaultDataProvider,
  authProvider = defaultAuthProvider,
  disableTelemetry,
  ...rest
}: CRMProps) => {
  // const [useNewDashboard, setUseNewDashboard] = useState(false); // ← Add state
  const [useNewDashboard, setUseNewDashboard] = useState(() => {
    // Check URL parameter for dashboard version
    const params = new URLSearchParams(window.location.search);
    return params.get("dashboard") === "v2";
  });
  useEffect(() => {
    if (
      disableTelemetry ||
      process.env.NODE_ENV !== "production" ||
      typeof window === "undefined" ||
      typeof window.location === "undefined" ||
      typeof Image === "undefined"
    ) {
      return;
    }
    const img = new Image();
    img.src = `https://atomic-crm-telemetry.marmelab.com/atomic-crm-telemetry?domain=${window.location.hostname}`;
  }, [disableTelemetry]);


  const crmContent = (
    <ConfigurationProvider
      contactGender={contactGender}
      companySectors={companySectors}
      companyLifecycleStages={companyLifecycleStages}
      companyTypes={companyTypes}
      companyQualificationStatuses={companyQualificationStatuses}
      companyRevenueRanges={companyRevenueRanges}
      dealCategories={dealCategories}
      dealPipelineStatuses={dealPipelineStatuses}
      dealStages={dealStages}
      darkModeLogo={darkModeLogo}
      lightModeLogo={lightModeLogo}
      noteStatuses={noteStatuses}
      taskTypes={taskTypes}
      taskPriorities={taskPriorities}
      taskStatuses={taskStatuses}
      title={title}
      isDemo={rest.isDemo ?? import.meta.env.VITE_IS_DEMO === "true"}
      externalHeartbeatStatuses={externalHeartbeatStatuses}
      internalHeartbeatStatuses={internalHeartbeatStatuses}
    >
      <AdminContext
        dataProvider={dataProvider}
        authProvider={authProvider}
        store={localStorageStore(undefined, "neXus")}
        i18nProvider={i18nProvider}
        {...rest}
      >
        <DatabaseHealthCheck dataProvider={dataProvider}>
          <AISettingsProvider>
            <PasswordResetHandler />

            <AdminUI
              layout={Layout}
              loginPage={StartPage}
              dashboard={useNewDashboard ? DashboardV2 : Dashboard} // ← Toggle here
              requireAuth
              disableTelemetry
              {...rest}
            >
              <CustomRoutes noLayout>
                <Route path={SignupPage.path} element={<SignupPage />} />
                <Route
                  path={SetPasswordPage.path}
                  element={<SetPasswordPage />}
                />
                <Route
                  path={ForgotPasswordPage.path}
                  element={<ForgotPasswordPage />}
                />
                <Route
                  path={ChangePasswordPage.path}
                  element={<ChangePasswordPage />}
                />
                <Route path={OtpLoginPage.path} element={<OtpLoginPage />} />
                <Route path="/onboarding" element={<OnboardingWizard />} />

              </CustomRoutes>
              {/* Everyone can access My Info */}
              <CustomRoutes>
                <Route path={SettingsPage.path} element={<SettingsPage />} />
                <Route
                  path="/data-management"
                  element={<DataManagementPage />}
                />
              </CustomRoutes>
              {/* Admin-only routes */}
              <CanAccess
                resource="settings"
                action="list"
                fallback={<Navigate to="/" replace />}
              >
                <CustomRoutes>
                  <Route path={SettingsPage.path} element={<SettingsPage />} />
                  <Route path={DatabasePage.path} element={<DatabasePage />} />

                  <Route
                    path={IntegrationsPage.path}
                    element={<IntegrationsPage />}
                  />
                  <Route path="/ai" element={<AIPage />} />
                </CustomRoutes>
              </CanAccess>
              <Resource name="ai" list={AIPage} icon={Sparkles} />
              <Resource name="deals" {...deals} />
              <Resource name="invoices" {...invoices} />
              <Resource name="invoice_items" />
              <Resource name="invoice_notes" />
              <Resource name="invoices_summary" />
              <Resource name="tax_presets" />
              <Resource name="contacts" {...contacts} />
              <Resource name="companies" {...companies} />
              <Resource
                name="crm_activities"
                options={{ label: "Activities" }}
                {...activities}
              />{" "}
              <Resource name="sales" {...sales} />
              <Resource name="tags" />
              <CustomRoutes noLayout>
                <Route path="/setup-workspace" element={<SetupWorkspace />} />
              </CustomRoutes>
            </AdminUI>
          </AISettingsProvider>
        </DatabaseHealthCheck>
      </AdminContext>
    </ConfigurationProvider>
  );

  //  return <SDKProvider>{crmContent}</SDKProvider>;
  return <>{crmContent}</>;
};
